type VidaaTVEnvironment = 'production' | 'dev';

interface VidaaTVLoaderConfig {
  clientId: string;
  environment?: VidaaTVEnvironment;
}

class VidaaTVLoader {
  private clientId: string;
  private environment: VidaaTVEnvironment;
  private loadPromise: Promise<any> | null = null;
  private vidaaTVInstance: any = null;

  private readonly BASE_URLS = {
    production: 'https://tvmodules-vidaa.vidaahub.com/deviceapi',
    dev: 'https://tvmodules-vidaa-test.vidaahub.com/deviceapi',
  };

  constructor(config: VidaaTVLoaderConfig) {
    this.clientId = config.clientId;
    this.environment = config.environment || (process.env.VUE_APP_ENV as VidaaTVEnvironment) || 'production';
  }

  /**
   * Loads the latest version of VidaaTV API (Recommended)
   * @returns Promise that resolves with the VidaaTV object
   */
  async loadLatest(): Promise<any> {
    if (this.loadPromise) {
      return this.loadPromise;
    }

    const baseUrl = this.BASE_URLS[this.environment];
    const url = `${baseUrl}/vidaatv.js?from=${this.clientId}`;

    this.loadPromise = this.fetchAndExecuteScript(url);
    return this.loadPromise;
  }

  /**
   * Loads a specific version of VidaaTV API
   * @param version - Version string (e.g., "1.0.0")
   * @returns Promise that resolves with the VidaaTV object
   */
  async loadVersion(version: string): Promise<any> {
    if (this.loadPromise) {
      return this.loadPromise;
    }

    const baseUrl = this.BASE_URLS[this.environment];
    const url = `${baseUrl}/archive/${version}/vidaatv.js?from=${this.clientId}`;

    this.loadPromise = this.fetchAndExecuteScript(url);
    return this.loadPromise;
  }

  /**
   * Fetches and executes the script
   */
  private async fetchAndExecuteScript(url: string): Promise<any> {
    try {
      // Check if VidaaTV is already loaded
      if ((window as any).VidaaTV) {
        this.vidaaTVInstance = (window as any).VidaaTV;
        return this.vidaaTVInstance;
      }

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch VidaaTV script: ${response.status} ${response.statusText}`);
      }

      const scriptContent = await response.text();

      // Execute the script by creating a script element
      const script = document.createElement('script');
      script.textContent = scriptContent;
      document.head.appendChild(script);

      // Wait a tick for the script to execute
      await new Promise(resolve => setTimeout(resolve, 0));

      // Check if VidaaTV was added to window
      if ((window as any).VidaaTV) {
        this.vidaaTVInstance = (window as any).VidaaTV;
        // Clean up the script element
        document.head.removeChild(script);
        return this.vidaaTVInstance;
      }

      // Clean up the script element even on failure
      document.head.removeChild(script);

      throw new Error('VidaaTV object not found after script execution');
    } catch (error) {
      throw new Error(`Failed to load VidaaTV: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Fetches the raw script content without executing it
   * @returns The raw script content as a string
   */
  async getRawScript(): Promise<string> {
    const baseUrl = this.BASE_URLS[this.environment];
    const url = `${baseUrl}/vidaatv.js?from=${this.clientId}`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch script: ${response.status} ${response.statusText}`);
    }

    return response.text();
  }

  /**
   * Fetches raw script content for a specific version
   * @param version - Version string (e.g., "1.0.0")
   * @returns The raw script content as a string
   */
  async getRawScriptVersion(version: string): Promise<string> {
    const baseUrl = this.BASE_URLS[this.environment];
    const url = `${baseUrl}/archive/${version}/vidaatv.js?from=${this.clientId}`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch script: ${response.status} ${response.statusText}`);
    }

    return response.text();
  }

  /**
   * Gets the loaded VidaaTV instance
   * @returns The VidaaTV instance or null if not loaded
   */
  getInstance(): any {
    return this.vidaaTVInstance;
  }

  /**
   * Checks if VidaaTV has been loaded
   * @returns true if VidaaTV is loaded, false otherwise
   */
  isLoaded(): boolean {
    return this.vidaaTVInstance !== null;
  }

  /**
   * Switches the environment (only works if script hasn't been loaded yet)
   * @param environment - The environment to switch to
   */
  setEnvironment(environment: VidaaTVEnvironment): void {
    if (this.loadPromise) {
      console.warn('Cannot change environment after script has been loaded');
      return;
    }
    this.environment = environment;
  }

  /**
   * Gets the current environment
   * @returns The current environment
   */
  getEnvironment(): VidaaTVEnvironment {
    return this.environment;
  }

  /**
   * Resets the loader state
   * Removes the global VidaaTV object and clears internal state
   */
  reset(): void {
    this.loadPromise = null;
    this.vidaaTVInstance = null;

    // Remove the global VidaaTV object
    delete (window as any).VidaaTV;
  }
}

export default VidaaTVLoader;
export type { VidaaTVEnvironment, VidaaTVLoaderConfig };
